import { AppState } from 'src/app/core/core.state'

import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { OrganizationRequiredSubscriptionService } from './organization-required-subscription.service'
import { organizationActions } from './organization.actions'
import { concatMap, withLatestFrom, map } from 'rxjs/operators'
import { organizationSelectors } from './organization.selectors'
import { OrganizationDocumentService } from './organization-document.service'
import { documentActions } from '../document/document.actions'

@Injectable()
export class OrganizationRelationEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private organizationRequiredSubscriptionService: OrganizationRequiredSubscriptionService,
    private organizationDocumentService: OrganizationDocumentService
  ) {}

  createOneRequiredSubscriptionLink = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationActions.createOneRequiredSubscriptionLink),
      concatMap((action) => {
        return this.organizationRequiredSubscriptionService
          .createOne(action.organizationId, action.subscriptionId)
          .pipe(
            withLatestFrom(
              this.store.select(
                organizationSelectors.selectById,
                action.organizationId
              )
            ),
            map(([response, organization]) =>
              organizationActions.upsertOne({
                entity: {
                  ...organization,
                  requiredSubscriptions: [
                    ...(organization?.requiredSubscriptions ?? []),
                    action.subscriptionId
                  ]
                }
              })
            )
          )
      })
    )
  )

  deleteOneRequiredSubscriptionLink = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationActions.deleteOneRequiredSubscriptionLink),
      concatMap((action) =>
        this.organizationRequiredSubscriptionService
          .deleteOne(action.organizationId, action.subscriptionId)
          .pipe(
            withLatestFrom(
              this.store.select(
                organizationSelectors.selectById,
                action.organizationId
              )
            ),
            map(([response, organization]) =>
              organizationActions.upsertOne({
                entity: {
                  ...organization,
                  requiredSubscriptions: [
                    ...organization?.requiredSubscriptions?.filter(
                      (subscriptionId) =>
                        subscriptionId !== action.subscriptionId
                    )
                  ]
                }
              })
            )
          )
      )
    )
  )

  createOneDocumentLink = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationActions.createOneDocumentLink),
      concatMap((action) =>
        this.organizationDocumentService
          .createOne(action.organizationId, action.documentId)
          .pipe(
            withLatestFrom(
              this.store.select(
                organizationSelectors.selectById,
                action.organizationId
              )
            ),
            map(([response, organization]) =>
              organizationActions.upsertOne({
                entity: {
                  ...organization,
                  documents: [
                    ...(organization.documents ?? []),
                    action.documentId
                  ]
                }
              })
            )
          )
      )
    )
  )

  deleteOneDocumentLink = createEffect(() =>
    this.actions$.pipe(
      ofType(organizationActions.deleteOneDocumentLink),
      concatMap((action) =>
        this.organizationDocumentService
          .deleteOne(action.organizationId, action.documentId)
          .pipe(
            withLatestFrom(
              this.store.select(
                organizationSelectors.selectById,
                action.organizationId
              )
            ),
            map(([response, organization]) =>
              organizationActions.upsertOne({
                entity: {
                  ...organization,
                  documents: organization.documents?.length
                    ? [
                        ...organization.documents.filter(
                          (documentId) => documentId !== action.documentId
                        )
                      ]
                    : []
                }
              })
            )
          )
      )
    )
  )
}
